import { ed25519 } from '@noble/curves/ed25519';
import { secp256k1 } from '@noble/curves/secp256k1';
import type { DerivedKeys } from './derive';
import type { Addresses, Chain } from './types';

export interface VaultSnapshot {
  addresses: Addresses;
  credentialId: string;
}

export interface SessionVault {
  isOpen(): boolean;
  /** Public-safe view: never includes raw keys. */
  snapshot(): VaultSnapshot | null;
  /** Open the vault with derived keys + the addresses they yielded + the credentialId. */
  set(args: { keys: DerivedKeys; addresses: Addresses; credentialId: string }): void;
  /** Drop the keys and address state. Safe to call when already closed. */
  clear(): void;
  /**
   * Sign a payload with the chain-appropriate key. Spec §3.6 invariant:
   * this is the only way the rest of the package can reach the keys.
   *
   * - 'cere' / 'solana': Ed25519 signature over the raw payload (64 bytes).
   * - 'evm': secp256k1 ECDSA over the raw payload (65 bytes: r || s || v).
   *   The caller (an EVM signer) is responsible for framing (e.g. EIP-191)
   *   and hashing (keccak256) before passing the digest as the payload.
   */
  sign(chain: Chain, payload: Uint8Array): Promise<Uint8Array>;
}

/**
 * Closure-encapsulated session vault. The keys live in the closure variables
 * `state.edSeed` / `state.secpKey` and are not reachable from outside.
 *
 * Spec §3.4. Lifetime: from `set()` until `clear()` or tab unload.
 */
export function createSessionVault(): SessionVault {
  let state: {
    edSeed: Uint8Array;
    secpKey: Uint8Array;
    addresses: Addresses;
    credentialId: string;
  } | null = null;

  return {
    isOpen() {
      return state !== null;
    },
    snapshot() {
      if (!state) return null;
      return { addresses: state.addresses, credentialId: state.credentialId };
    },
    set({ keys, addresses, credentialId }) {
      state = {
        edSeed: new Uint8Array(keys.edSeed),
        secpKey: new Uint8Array(keys.secpKey),
        addresses,
        credentialId,
      };
    },
    clear() {
      if (state) {
        state.edSeed.fill(0);
        state.secpKey.fill(0);
      }
      state = null;
    },
    async sign(chain, payload) {
      if (!state) {
        throw new Error('SessionVault: closed — no session keys available');
      }
      if (chain === 'cere' || chain === 'solana') {
        return ed25519.sign(payload, state.edSeed);
      }
      if (chain === 'evm') {
        // Caller (chain-specific signer) is responsible for framing + hashing.
        // We sign the payload directly and return 65 bytes: r (32) || s (32) || v (1),
        // where v is the recovery byte (0 or 1). Spec §3.6 invariant 1 holds —
        // the secpKey never leaves this closure.
        const sig = secp256k1.sign(payload, state.secpKey);
        const compact = sig.toCompactRawBytes(); // 64 bytes (r || s)
        const v = sig.recovery; // 0 or 1
        const out = new Uint8Array(65);
        out.set(compact, 0);
        out[64] = v;
        return out;
      }
      throw new Error(`SessionVault.sign: unknown chain "${String(chain)}"`);
    },
  };
}
